
import { defineComponent, inject } from 'vue'
import DatePicker from 'vue3-datepicker'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { BuildTypeService } from '@/services/buildType.service'
import NoteTile from '@/components/elements/NoteTile.vue'
import { Note } from '@/@types/responses/note/Note'
import NoteModal from '@/components/modals/NoteModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import TextArea from '@/components/shared/TextArea.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { NoteService } from '@/services/note.service'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { OrderService } from '@/services/order.service'
import { OrderDetails } from '@/@types/responses/order/OrderDetails'
import { OrderBuildStepDetails } from '@/@types/responses/order/OrderBuildStepDetails'
import Checkbox from '@/components/shared/Checkbox.vue'
import { OrderServiceDetails } from '@/@types/responses/order/OrderServiceDetails'
import AddPartModal from '@/components/modals/AddPartModal.vue'
import AddServiceModal from '@/components/modals/AddServiceModal.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import PlusSvg from '@/components/svgs/PlusSvg.vue'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import CommentSvg from '@/components/svgs/CommentSvg.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { OrderPartService } from '@/services/orderPart.service'
import { OrderServiceService } from '@/services/orderService.service'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { OrderBuildStepService } from '@/services/orderBuildStep.service'
import { UpdateOrderMessage } from '@/@types/messages/UpdateOrderMessage'
import { UpdateOrderServiceMessage } from '@/@types/messages/UpdateOrderServiceMessage'
import { UpdateOrderBuildStepMessage } from '@/@types/messages/UpdateOrderBuildStepMessage'
import { UpdateOrderPartMessage } from '@/@types/messages/UpdateOrderPartMessage'
import { OrderPartDetails } from '@/@types/responses/order/OrderPartDetails'
import LockingButton from '@/components/shared/LockingButton.vue'
import moment from 'moment'
import { OrderPaymentMessage } from '@/@types/messages/OrderPaymentMessage'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import DragAndDropSvg from '@/components/svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import { UpdateOrderPartsMessage } from '@/@types/messages/UpdateOrderPartsMessage'
import { UpdateOrderServicesMessage } from '@/@types/messages/UpdateOrderServicesMessage'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Order',
  components: {
    AddPartModal,
    AddServiceModal,
    AuditInformation,
    CartridgeSearchMachine,
    Checkbox,
    CommentSvg,
    CurrencyInput,
    DatePicker,
    DotsSvg,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    NumericInput,
    PlusSvg,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showAddPartModal || this.showAddServiceModal || this.showNoteModal || this.showOrderPartNoteModal || this.showOrderServiceNoteModal) {
      this.showAddPartModal = false
      this.showAddServiceModal = false
      this.showNoteModal = false
      this.showOrderPartNoteModal = false
      this.showOrderServiceNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const orderBuildStepService = inject(PROVIDER_CONSTANTS.orderBuildStepService) as OrderBuildStepService
    const orderPartService = inject(PROVIDER_CONSTANTS.orderPartService) as OrderPartService
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const orderServiceService = inject(PROVIDER_CONSTANTS.orderServiceService) as OrderServiceService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildTypeService,
      noteService,
      orderBuildStepService,
      orderPartService,
      orderService,
      orderServiceService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let order: OrderDetails|undefined
    let orderPartNoteToEdit: Note|undefined
    let orderServiceNoteToEdit: Note|undefined
    let scheduledDate: Date|undefined
    let unchangedOrder: OrderDetails|undefined

    return {
      addingNote: false,
      addingPayment: false,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      creatingInvoice: false,
      creatingQuote: false,
      loading: true,
      newNoteText: '',
      newPaymentAmount: 0,
      noteToEdit,
      order,
      orderPartId: 0,
      orderPartNoteToEdit,
      orderServiceId: 0,
      orderServiceNoteToEdit,
      saving: false,
      scheduledDate,
      showAddPartModal: false,
      showAddServiceModal: false,
      showMenu: false,
      showNoteModal: false,
      showOrderPartNoteModal: false,
      showOrderServiceNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedOrder,
      updatingInvoice: false
    }
  },
  computed: {
    addNoteEnabled(): boolean {
      return this.newNoteText.trim() !== ''
    },
    addPaymentEnabled(): boolean {
      return this.newPaymentAmount > 0
    },
    cartridgeRequired(): boolean {
      const index = this.buildTypeDropdownOptions.findIndex(b => b.id === this.order?.buildTypeId)
      if (index === -1)
        return false

      return this.buildTypeDropdownOptions[index].cartridgeRequired
    },
    isDirty(): boolean {
      const a = this.order
      const b = this.unchangedOrder

      let validCartridgeValue = true
      if (this.cartridgeRequired && !a?.cartridgeId)
        validCartridgeValue = false

      return (a?.cartridgeId !== b?.cartridgeId ||
             a?.customerId !== b?.customerId ||
             a?.onHold !== b?.onHold ||
             a?.scheduledDate !== b?.scheduledDate ||
             a?.buildTypeId !== b?.buildTypeId ||
             a?.partsDiscountPercentage !== b?.partsDiscountPercentage ||
             a?.servicesDiscountPercentage !== b?.servicesDiscountPercentage ||
             a?.customerSuppliedParts !== b?.customerSuppliedParts) && (a?.customerId !== undefined && validCartridgeValue)
    },
    stateDisplayValue(): string|undefined {
      if (this.order && this.order.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.order?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    },
    titleText(): string {
      return `Order #${this.id}`
    }
  },
  mounted() {
    Promise.all([this.getBuildTypes(), this.getStates()]).then(() => {
      this.getOrder()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getBuildTypes(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getOrder(): Promise<void> {
      return this.orderService.getOrder(this.order ? this.order.id : this.id).then(order => {
        this.order = order
        this.unchangedOrder = JSON.parse(JSON.stringify(order))
        this.updatePageTitle()
        this.scheduledDate = order.scheduledDate ? moment.utc(order.scheduledDate).local().toDate() : undefined 
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the order. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onAddNoteClicked(): void {
      this.addingNote = true
      const noteMessage = new NoteMessage(this.newNoteText, false)
      this.orderService.createOrderNote(this.id, noteMessage).then(() => {
        this.getOrder().then(() => {
          this.newNoteText = ''
          this.addingNote = false
          this.toast.success('Note added successfully.')
        })
      }).catch(error => {
        this.addingNote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to create the note. Please try again.')
        }
      })
    },
    onAddOrderPartNoteClicked(orderPartId: number): void {
      this.orderPartId = orderPartId
      this.orderPartNoteToEdit = new Note(0)
      this.showOrderPartNoteModal = true
    },
    onAddPartClicked(): void {
      this.showAddPartModal = true
    },
    onAddPaymentClicked(): void {
      this.addingPayment = true
      const orderPaymentMessage = new OrderPaymentMessage(this.newPaymentAmount)
      this.orderService.createOrderPayment(this.id, orderPaymentMessage).then(() => {
        this.getOrder().then(() => {
          this.newPaymentAmount = 0
          this.addingPayment = false
          this.toast.success('Payment added successfully.')
        })
      }).catch(error => {
        this.addingPayment = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to add the payment. Please try again.')
        }
      })
    },
    onAddServiceClicked(): void {
      this.showAddServiceModal = true
    },
    onAddServiceNoteClicked(orderServiceId: number): void {
      this.orderServiceId = orderServiceId
      this.orderServiceNoteToEdit = new Note(0)
      this.showOrderServiceNoteModal = true
    },
    onCreateInvoiceClicked(): void {
      this.creatingInvoice = true

      this.orderService.createOrderInvoice(this.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Invoice created successfully.')
          this.creatingInvoice = false
        })
      }).catch(error => {
        this.creatingInvoice = false
        
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to create the invoice. Please try again.')
        }
      })
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDuplicateClicked(): void {
      this.creatingQuote = true

      this.orderService.createQuoteFromOrder(this.id).then(objectCreatedResponse => {
        this.toast.success('A new quote was created from the existing order successfully.')
        this.$router.push('/quote/' + objectCreatedResponse.id)
      }).catch(error => {
        this.creatingQuote = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to duplicate the order as a new quote. Please try again.')
        }
      })
    },
    onEditCustomerClicked(): void {
      if (!this.order || !this.order.customerId) return

      const routeData = this.$router.resolve('/customer/' + this.order.customerId)
      window.open(routeData.href, '_blank')
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onEditOrderPartNote(note: Note): void {
      this.orderPartNoteToEdit = note
      this.showOrderPartNoteModal = true
    },
    onEditOrderServiceNote(note: Note): void {
      this.orderServiceNoteToEdit = note
      this.showOrderServiceNoteModal = true
    },
    onOrderBuildStepStatusChanged(orderBuildStepId: number, buildStepStatus: number): void {
      const updateOrderBuildStepMessage = new UpdateOrderBuildStepMessage(buildStepStatus)
      this.orderBuildStepService.updateOrderBuildStep(orderBuildStepId, updateOrderBuildStepMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Build step status updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the build step status. Please try again.')
        }
      })
    },
    onOrderPartChanged(orderPartId: number, action: number|undefined, price: number, purchaseOrderItemId: number|undefined): void {
      const updateOrderPartMessage = new UpdateOrderPartMessage(action, price, purchaseOrderItemId)
      this.orderPartService.updateOrderPart(orderPartId, updateOrderPartMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onOrderPartsDisplayOrderChanged(): void {
      if (!this.order) return

      const updateOrderPartsMessage = new UpdateOrderPartsMessage(this.order.orderParts.map(o => o.id))
      this.orderPartService.updateOrderParts(updateOrderPartsMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the part. Please try again.')
        }
      })
    },
    onOrderServiceChanged(orderService: OrderServiceDetails): void {
      const updateOrderServiceMessage = new UpdateOrderServiceMessage(orderService.completed, orderService.price)
      this.orderServiceService.updateOrderService(orderService.id, updateOrderServiceMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onOrderServicesDisplayOrderChanged(): void {
      if (!this.order) return

      const updateOrderServicesMessage = new UpdateOrderServicesMessage(this.order.orderServices.map(o => o.id))
      this.orderServiceService.updateOrderServices(updateOrderServicesMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the service. Please try again.')
        }
      })
    },
    onRemoveOrderPartClicked(id: number): void {
      if (!this.order) return

      this.orderPartService.deleteOrderPart(id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The part was removed from the order successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the part from the order. Please try again.')
        }
      })
    },
    onRemoveOrderServiceClicked(id: number): void {
      if (!this.order) return

      this.orderServiceService.deleteOrderService(id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The service was removed from the order successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the service from the order. Please try again.')
        }
      })
    },
    onSaveOrderClicked(): void {
      if (!this.order || !this.order.customerId) return
      
      this.saving = true

      const utcScheduledDate = this.scheduledDate ? moment.utc(this.scheduledDate).format() : undefined
      const updateOrderMessage = new UpdateOrderMessage(this.order.buildTypeId, this.order.cartridgeId, this.order.customerSuppliedParts, this.order.onHold, this.order.partsDiscountPercentage, utcScheduledDate, this.order.servicesDiscountPercentage)
      this.orderService.updateOrder(this.id, updateOrderMessage).then(() => {
        this.getOrder().then(() => {
          this.toast.success('The order was updated successfully.')
          this.saving = false
        })
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the order. Please try again.')
        }
      })
    },
    onScheduledDateChanged(): void {
      const element = document.getElementById('scheduled-date')
      element?.blur()

      if (!this.order) return

      this.order.scheduledDate = this.scheduledDate ? moment.utc(this.scheduledDate).format() : undefined
    },
    onUpdateInvoiceClicked(): void {
      this.updatingInvoice = true

      this.orderService.updateOrderInvoice(this.id).then(() => {
        this.getOrder().then(() => {
          this.toast.success('Invoice updated successfully.')
          this.updatingInvoice = false
        })
      }).catch(error => {
        this.updatingInvoice = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the invoice. Please try again.')
        }
      })
    },
    orderPartIdsFromOrderPart(orderPart: OrderPartDetails): number[] {
      if (!this.order) return []

      const orderParts = this.order.orderParts.filter(op => op.inventoryItemId === orderPart.inventoryItemId)
      return orderParts.map(op => op.id)
    },
    orderPartStatusToDisplayValue(status: number): string {
      switch(status) {
        case 1: return 'Needs Allocated'
        case 2: return 'Purchase Order Created'
        case 3: return 'Purchase Order Submitted'
        case 4: return 'Purchase Order Verified'
        case 5: return 'Pulled From Inventory'
        default: return ''
      }
    },
    statusText(orderBuildStep: OrderBuildStepDetails): string {
      switch(orderBuildStep.processStatus) {
        case 1: return 'Pending'
        case 2: return 'In Progress'
        case 3: return `Completed by ${orderBuildStep.completedByUser?.firstName} ${orderBuildStep.completedByUser?.lastName}`
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.order)
        document.title = `Order #${this.order.id} - Short Action Customs, LLC`
    }
  }
})
